import React, { useEffect, useRef, useState } from 'react';
import { Form } from 'react-final-form';
import { Button, FormHelperText, FormLabel, makeStyles, Typography } from '@material-ui/core';
import moment, { Moment } from 'moment';
import { useDataProvider, useNotify, useRefresh } from 'react-admin';
import { FORM_ERROR, setIn } from 'final-form';

import { Dialog } from 'components/common';
import { Checkbox, Input, Select, Switch } from 'components/form';
import { required } from 'components/form/validationRules';

import TypeChooser from './TypeChooser';
import { CreditCodeType, DeliveryService } from '../types';
import DateInputField from './DateInputField';
import TimeInputField from './TimeInputField';
import { getCurrencySymbol, SUPPORTED_CURRENCIES } from 'services/helpers/currency';

const useStyles = makeStyles((theme) => ({
  dialog: {
    '&&': {
      maxWidth: 650,
    },
  },
  dialogContent: {
    '&&': {
      minWidth: 0,
    },
  },
  dialogError: {
    '&&': {
      padding: 0,
      margin: theme.spacing(2, 4, 0),
      maxHeight: theme.spacing(20),
      overflowY: 'auto',
      flexShrink: 0,
    },
  },
  errorSummary: {
    border: `1px solid ${theme.palette.error.main}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(1.5),
    overflowWrap: 'break-word',
  },
  errorHeading: {
    fontWeight: 600,
  },
  errorList: {
    margin: theme.spacing(0.5, 0, 0),
    paddingLeft: theme.spacing(2.5),
  },
  generalError: {
    marginTop: theme.spacing(1),
  },
  inputsContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
    '& > *': {
      flex: 1,
      minWidth: 0,
    },
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  },
  datePickerContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flexWrap: 'wrap',
  },
  date: {
    padding: theme.spacing(3, 0, 1.5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: theme.spacing(1),
    },
  },
  invalidLabel: {
    color: theme.palette.error.main,
  },
  invalidField: {
    '& .MuiFormLabel-root, & .MuiFormControlLabel-label': {
      color: theme.palette.error.main,
    },
  },
  invalidType: {
    outline: `1px solid ${theme.palette.error.main}`,
    borderRadius: theme.shape.borderRadius,
  },
  actions: {
    '& .MuiButton-label': {
      textTransform: 'none',
    },
  },
  input: {
    '& .MuiInput-input': {
      padding: theme.spacing(1, 0),
    },
  },
  inputRoot: {
    width: '100%',
  },
  deferred: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1, 0, 0),
    borderTop: '1px solid #E0E0E0',
  },
  hint: {
    color: theme.palette.text.secondary,
    fontSize: 12,
  },
  excludedServicesHeader: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    columnGap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  excludedServicesLabel: {
    color: theme.palette.secondary.contrastText,
    fontSize: 12,
  },
  excludedServicesControl: {
    '&&': {
      marginTop: 0,
    },
    '& .MuiInputLabel-root': {
      display: 'none',
    },
    '& .MuiInput-root': {
      marginTop: 0,
    },
  },
  excludedServicesSelect: {
    '&&': {
      height: 'auto',
      whiteSpace: 'normal',
      textOverflow: 'clip',
      overflow: 'visible',
      paddingRight: theme.spacing(3),
    },
  },
  excludedServicesValues: {
    display: 'flex',
    flexWrap: 'wrap',
    columnGap: theme.spacing(0.5),
  },
  excludedService: {
    flexShrink: 0,
    minWidth: 0,
    maxWidth: '100%',
    overflowWrap: 'break-word',
  },
}));

interface Props {
  open: boolean;
  onCancel: () => void;
}

interface FormValues {
  type: CreditCodeType;
  name: string;
  code: string;
  numberOfCodes: string;
  priceOfCodes: string;
  redeemLimitOfCodes: string;
  startAtDate: { date: Moment | null; time: Moment | null };
  endAtDate: { date: Moment | null; time: Moment | null };
  currency: string;
  deferredEnabled: boolean;
  deferred: {
    spendThreshold: string;
    earnWindowDays: string;
    requireVerifiedCard: boolean;
    excludeInternational: boolean;
    excludedServices: string[];
  };
}

interface DeliveryServicesState {
  items: DeliveryService[];
  status: 'loading' | 'loaded' | 'error';
  error?: string;
}

interface CampaignErrors {
  fields: Record<string, string>;
  general: string[];
}

const DATE_ORDER_FIELD = 'startDateBeforeEndDate';
const DATE_ORDER_MESSAGE = 'Choose an end date and time later than the start.';
const CREATE_ERROR_MESSAGE = 'Unable to create the campaign. Please try again.';

const fieldLabels: Record<string, string> = {
  type: 'Campaign type',
  name: 'Campaign name',
  currency: 'Currency',
  priceOfCodes: 'Amount',
  numberOfCodes: 'Number of codes',
  redeemLimitOfCodes: 'Usage limit per code (0 = unlimited)',
  code: 'Custom code',
  startAtDate: 'Start date',
  endAtDate: 'End date',
  [DATE_ORDER_FIELD]: 'Start and end dates',
  'deferred.spendThreshold': 'Spend on labels before the credit is granted',
  'deferred.earnWindowDays': 'Days after signup to earn it',
  'deferred.requireVerifiedCard': 'Require a verified card',
  'deferred.excludeInternational': 'International labels do not count',
  'deferred.excludedServices': 'Services that do not count',
};

const textFields = [
  'name',
  'priceOfCodes',
  'numberOfCodes',
  'redeemLimitOfCodes',
  'code',
  'deferred.spendThreshold',
  'deferred.earnWindowDays',
];

const errorId = (field: string) => `campaign-${field.replace(/\./g, '-')}-error`;

const isDateError = (field: string) =>
  field === DATE_ORDER_FIELD || field === 'startAtDate' || field === 'endAtDate';

const relatedFields = (field: string) => {
  if (field === DATE_ORDER_FIELD) {
    return ['startAtDate.date', 'startAtDate.time', 'endAtDate.date', 'endAtDate.time'];
  }

  return isDateError(field) ? [`${field}.date`, `${field}.time`] : [field];
};

const isFieldVisible = (field: string, values: FormValues) => {
  const isFund = values.type === CreditCodeType.Fund;

  return (
    (field !== 'currency' || isFund) &&
    (!field.startsWith('deferred.') || (isFund && values.deferredEnabled))
  );
};

const getErrorMessage = (error: any) => {
  if (typeof error?.body?.detail === 'string' && error.body.detail.trim()) {
    return error.body.detail;
  }

  if (typeof error?.message !== 'string' || !error.message.trim()) {
    return CREATE_ERROR_MESSAGE;
  }

  try {
    const payload = JSON.parse(error.message);
    const message =
      typeof payload === 'string' ? payload : payload?.message || payload?.error?.message;

    return typeof message === 'string' && message.trim() ? message : CREATE_ERROR_MESSAGE;
  } catch (parseError) {
    return error.message;
  }
};

const getSubmissionErrors = (error: any, values: FormValues) => {
  const groupedErrors = new Map<string | number, string[]>();

  if (Array.isArray(error?.body?.errors)) {
    error.body.errors.forEach((validationError, index) => {
      const description =
        validationError?.field === DATE_ORDER_FIELD
          ? DATE_ORDER_MESSAGE
          : validationError?.description;

      if (typeof description !== 'string' || !description.trim()) {
        return;
      }

      const field =
        typeof validationError.field === 'string' && validationError.field.trim()
          ? validationError.field
          : index;
      const descriptions = groupedErrors.get(field) || [];

      if (field !== DATE_ORDER_FIELD || !descriptions.length) {
        descriptions.push(description);
      }

      groupedErrors.set(field, descriptions);
    });
  }

  const errors: CampaignErrors = { fields: {}, general: [] };

  groupedErrors.forEach((descriptions, field) => {
    const message = descriptions.join(', ');

    if (typeof field === 'string' && Object.prototype.hasOwnProperty.call(fieldLabels, field)) {
      errors.fields[field] = message;
    } else {
      errors.general.push(message);
    }
  });

  if (!Object.keys(errors.fields).length && !errors.general.length) {
    errors.general.push(getErrorMessage(error));
  }

  return Object.keys(errors.fields).reduce<object>(
    (submissionErrors, field) =>
      isDateError(field) || !isFieldVisible(field, values)
        ? submissionErrors
        : setIn(submissionErrors, field, errors.fields[field]),
    { [FORM_ERROR]: errors }
  );
};

const requiredSelection = (selection: string) => (value: Moment | null) =>
  required(value) ? `Select ${selection.startsWith('end') ? 'an' : 'a'} ${selection}.` : undefined;

const requiredStartDate = requiredSelection('start date');
const requiredStartTime = requiredSelection('start time');
const requiredEndDate = requiredSelection('end date');
const requiredEndTime = requiredSelection('end time');

const wholeNumber = (minimum: number) => (value?: string) => {
  const message =
    minimum === 0 ? 'Must be a whole number of zero or greater' : 'Must be a positive whole number';

  if (!value || !/^\d+$/.test(value)) {
    return message;
  }

  const number = Number(value);

  if (!Number.isSafeInteger(number)) {
    return 'Value is too large';
  }

  return number >= minimum ? undefined : message;
};

const positiveWholeNumber = wholeNumber(1);
const nonNegativeWholeNumber = wholeNumber(0);

const validateCustomCode = (value?: string) => {
  const code = value?.trim();

  return code && !/^[a-zA-Z0-9]{4,20}$/.test(code)
    ? 'Must be 4 to 20 letters or digits'
    : undefined;
};

const validateNumberOfCodes = (value: string, values: FormValues) =>
  positiveWholeNumber(value) ||
  (values.code?.trim() && Number(value) !== 1
    ? 'Custom codes require exactly one code'
    : undefined);

const defaultValues: FormValues = {
  type: CreditCodeType.Fund,
  name: '',
  code: '',
  numberOfCodes: '',
  priceOfCodes: '',
  redeemLimitOfCodes: '',
  startAtDate: {
    date: null,
    time: null,
  },
  endAtDate: {
    date: null,
    time: null,
  },
  currency: 'USD',
  deferredEnabled: false,
  deferred: {
    spendThreshold: '',
    earnWindowDays: '30',
    requireVerifiedCard: true,
    excludeInternational: true,
    excludedServices: [],
  },
};

const getDate = ({ date, time }: { date: Moment | null; time: Moment | null }) => {
  if (!date || !time || !date.isValid() || !time.isValid()) {
    throw new Error('Select a valid date and time');
  }

  const selectedDateTime = `${date.format('YYYY-MM-DD')}T${time.format('HH:mm')}`;
  const dateTime = moment(selectedDateTime, 'YYYY-MM-DDTHH:mm', true);

  if (!dateTime.isValid()) {
    throw new Error('Select a valid date and time');
  }

  if (dateTime.format('YYYY-MM-DDTHH:mm') !== selectedDateTime) {
    throw new Error('Selected time does not exist on this date. Please choose another time');
  }

  return dateTime.format('YYYY-MM-DDTHH:mm:ss.SSSZ');
};

const validateCampaignDates = (values: FormValues) => {
  const errors: CampaignErrors = { fields: {}, general: [] };
  const dates: Partial<Record<'startAtDate' | 'endAtDate', Moment>> = {};
  const dateFields: Array<'startAtDate' | 'endAtDate'> = ['startAtDate', 'endAtDate'];
  const today = moment();

  dateFields.forEach((field) => {
    const value = values[field];

    if (!value?.date || !value?.time) {
      return;
    }

    try {
      dates[field] = moment(getDate(value));

      if (dates[field]?.isBefore(today)) {
        errors.fields[field] =
          `Choose ${field === 'startAtDate' ? 'a start' : 'an end'} date and time in the future.`;
      }
    } catch (error) {
      errors.fields[field] = error?.message || 'Select a valid date and time.';
    }
  });

  if (
    dates.startAtDate &&
    dates.endAtDate &&
    !errors.fields.startAtDate &&
    !errors.fields.endAtDate &&
    !dates.startAtDate.isBefore(dates.endAtDate)
  ) {
    errors.fields[DATE_ORDER_FIELD] = DATE_ORDER_MESSAGE;
  }

  return Object.keys(errors.fields).length ? { [FORM_ERROR]: errors } : {};
};

const fundCurrencies = SUPPORTED_CURRENCIES.map((item) => ({ value: item, label: item }));

const SERVICE_SEPARATOR = '|';

const serviceValue = ({ provider, code }: DeliveryService) =>
  `${provider}${SERVICE_SEPARATOR}${code}`;

const toExcludedService = (value: string) => {
  const [provider, serviceCode] = value.split(SERVICE_SEPARATOR);

  return { provider, serviceCode };
};

const CreateCampaignDialog = ({ open, onCancel }: Props) => {
  const classes = useStyles();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const [deliveryServicesState, setDeliveryServicesState] = useState<DeliveryServicesState>({
    items: [],
    status: 'loading',
  });
  const [deliveryServicesRetry, setDeliveryServicesRetry] = useState(0);
  const deliveryServicesRequestId = useRef(0);

  useEffect(() => {
    let isMounted = true;
    const requestId = ++deliveryServicesRequestId.current;
    const isCurrentRequest = () => isMounted && requestId === deliveryServicesRequestId.current;

    setDeliveryServicesState((state) => ({ ...state, status: 'loading', error: undefined }));

    const fetchDeliveryServices = async () => {
      try {
        const { data } = await dataProvider.get('deliveryServices');

        if (!isCurrentRequest()) {
          return;
        }

        if (
          !Array.isArray(data) ||
          !data.every(
            (service) =>
              service &&
              typeof service.provider === 'string' &&
              !!service.provider.trim() &&
              typeof service.code === 'string' &&
              !!service.code.trim()
          )
        ) {
          throw new Error('Invalid carrier services response');
        }

        setDeliveryServicesState({ items: data, status: 'loaded' });
      } catch (error) {
        if (!isCurrentRequest()) {
          return;
        }

        const message = error?.message || 'Failed to load carrier services';
        setDeliveryServicesState((state) => ({ ...state, status: 'error', error: message }));
        notify(message, 'warning');
      }
    };

    fetchDeliveryServices();

    return () => {
      isMounted = false;
    };
  }, [dataProvider, notify, deliveryServicesRetry]);

  const handleRetryDeliveryServices = () => {
    if (deliveryServicesState.status !== 'error') {
      return;
    }

    setDeliveryServicesState((state) => ({ ...state, status: 'loading', error: undefined }));
    setDeliveryServicesRetry((retry) => retry + 1);
  };

  const servicesByValue = new Map<string, DeliveryService>();

  deliveryServicesState.items.forEach((service) => {
    const value = serviceValue(service);
    const existing = servicesByValue.get(value);
    const label = typeof service.label === 'string' ? service.label.trim() : '';

    if (!existing || (!existing.label && label)) {
      servicesByValue.set(value, { ...service, label });
    }
  });

  const serviceOptions = Array.from(servicesByValue.values()).map((service) => ({
    value: serviceValue(service),
    label: `${service.provider} — ${service.label || service.code}`,
  }));

  const handleSubmitForm = async (values: FormValues) => {
    try {
      if (
        values.type === CreditCodeType.Fund &&
        values.deferredEnabled &&
        deliveryServicesState.status !== 'loaded'
      ) {
        throw new Error(
          deliveryServicesState.error ||
            'Wait for carrier services to load before creating a deferred campaign'
        );
      }

      const dateErrors = validateCampaignDates(values);

      if (Object.keys(dateErrors).length) {
        return dateErrors;
      }

      const startAtDate = getDate(values.startAtDate);
      const endAtDate = getDate(values.endAtDate);

      const { deferredEnabled, deferred, code, ...campaign } = values;

      const requestData = {
        ...campaign,
        code: code?.trim() ? code.trim() : undefined,
        currency: values.type === CreditCodeType.Fund ? values.currency : 'LABEL',
        startAtDate: startAtDate,
        endAtDate: endAtDate,
        deferred:
          deferredEnabled && values.type === CreditCodeType.Fund
            ? {
                spendThreshold: Number(deferred.spendThreshold),
                earnWindowDays: Number(deferred.earnWindowDays),
                requireVerifiedCard: deferred.requireVerifiedCard,
                excludeInternational: deferred.excludeInternational,
                excludedServices: (deferred.excludedServices || []).map(toExcludedService),
              }
            : undefined,
      };

      const response = await dataProvider.post('creditCodes', { data: requestData });

      onCancel();

      refresh();

      return response;
    } catch (error) {
      return getSubmissionErrors(error, values);
    }
  };

  return (
    <Form<FormValues>
      initialValues={defaultValues}
      onSubmit={handleSubmitForm}
      validate={validateCampaignDates}
      render={({ handleSubmit, submitting, values, error, submitError, submitFailed, form }) => {
        const isFund = values.type === CreditCodeType.Fund;
        const excludedServices = values.deferred.excludedServices || [];
        const allServicesSelected =
          serviceOptions.length > 0 &&
          serviceOptions.every((option) => excludedServices.includes(option.value));
        const submissionError = submitError as CampaignErrors | undefined;
        const validationError = submitFailed ? (error as CampaignErrors | undefined) : undefined;
        const submissionFields: Record<string, string> = {};
        const generalErrors = [...(submissionError?.general || [])];

        Object.keys(submissionError?.fields || {}).forEach((field) => {
          const isConditional = field === 'currency' || field.startsWith('deferred.');

          if (
            isConditional &&
            (form.getFieldState('type')?.dirtySinceLastSubmit ||
              (field.startsWith('deferred.') &&
                form.getFieldState('deferredEnabled')?.dirtySinceLastSubmit))
          ) {
            return;
          }

          if (!isFieldVisible(field, values)) {
            generalErrors.push(`${fieldLabels[field]}: ${submissionError!.fields[field]}`);
            return;
          }

          if (
            relatedFields(field).every((name) => {
              const state = form.getFieldState(name);

              return state && !state.dirtySinceLastSubmit;
            })
          ) {
            submissionFields[field] = submissionError!.fields[field];
          }
        });

        const getClientError = (name: string) => {
          const state = form.getFieldState(name);

          return state?.touched && typeof state.error === 'string' ? state.error : undefined;
        };

        const dateErrors = {
          ...submissionFields,
          ...validationError?.fields,
        };
        const visibleErrors: Record<string, string> = { ...dateErrors };

        Object.keys(fieldLabels).forEach((field) => {
          if (!isFieldVisible(field, values) || field === DATE_ORDER_FIELD) {
            return;
          }

          const messages = relatedFields(field).map(getClientError).filter(Boolean);

          if (messages.length) {
            visibleErrors[field] = messages.join(', ');
          }
        });

        const inputAccessibility = (name: string) => ({
          id: `campaign-${name.replace(/\./g, '-')}`,
          inputProps: {
            'aria-describedby': visibleErrors[name] ? errorId(name) : undefined,
          },
        });
        const renderControlError = (name: string) =>
          submissionFields[name] && !getClientError(name) ? (
            <FormHelperText error id={errorId(name)}>
              {submissionFields[name]}
            </FormHelperText>
          ) : null;
        const startDateError = dateErrors.startAtDate;
        const endDateError = dateErrors.endAtDate;
        const dateOrderError = dateErrors[DATE_ORDER_FIELD];
        const startDescriptionId =
          [startDateError && errorId('startAtDate'), dateOrderError && errorId(DATE_ORDER_FIELD)]
            .filter(Boolean)
            .join(' ') || undefined;
        const endDescriptionId =
          [endDateError && errorId('endAtDate'), dateOrderError && errorId(DATE_ORDER_FIELD)]
            .filter(Boolean)
            .join(' ') || undefined;
        const invalidFields = Object.keys(visibleErrors);
        const errorSummary =
          invalidFields.length || generalErrors.length ? (
            <div role="alert" className={classes.errorSummary}>
              <Typography variant="body2" className={classes.errorHeading}>
                {invalidFields.length
                  ? 'Please check the highlighted fields'
                  : 'Campaign could not be created'}
              </Typography>
              {!!invalidFields.length && (
                <ul className={classes.errorList}>
                  {invalidFields.map((field) => (
                    <li key={field}>{fieldLabels[field]}</li>
                  ))}
                </ul>
              )}
              {generalErrors.map((message, index) => (
                <Typography key={index} variant="body2" className={classes.generalError}>
                  {message}
                </Typography>
              ))}
            </div>
          ) : undefined;

        return (
          <Dialog
            open={open}
            maxWidth="md"
            fullWidth
            onCancel={onCancel}
            title="Create campaign"
            onConfirm={handleSubmit}
            submitting={submitting}
            confirmDisabled={
              isFund && values.deferredEnabled && deliveryServicesState.status !== 'loaded'
            }
            classes={{
              root: classes.dialog,
              content: classes.dialogContent,
              actions: classes.actions,
              error: classes.dialogError,
            }}
            confirmText="Create"
            error={errorSummary}>
            <div
              role="group"
              aria-label="Campaign type"
              aria-describedby={visibleErrors.type ? errorId('type') : undefined}
              className={visibleErrors.type ? classes.invalidType : undefined}>
              <TypeChooser name="type" />
              {renderControlError('type')}
            </div>

            <Input
              name="name"
              label="Campaign name"
              validate={required}
              inputProps={inputAccessibility('name')}
            />

            <div className={classes.inputsContainer}>
              {isFund && (
                <div className={visibleErrors.currency ? classes.invalidField : undefined}>
                  <Select
                    name="currency"
                    label="Currency"
                    options={fundCurrencies}
                    validate={required}
                    inputProps={{
                      error: Boolean(visibleErrors.currency),
                      SelectDisplayProps: {
                        'aria-label': `Currency: ${values.currency}`,
                        'aria-labelledby': undefined,
                        'aria-invalid': Boolean(visibleErrors.currency),
                        'aria-describedby': submissionFields.currency
                          ? errorId('currency')
                          : undefined,
                      },
                      inputProps: {
                        name: 'currency',
                      },
                    }}
                  />
                  {renderControlError('currency')}
                </div>
              )}

              <Input
                name="priceOfCodes"
                label="Amount"
                validate={nonNegativeWholeNumber}
                classes={{ input: classes.input, root: classes.inputRoot }}
                inputProps={{
                  ...inputAccessibility('priceOfCodes'),
                  InputProps: {
                    startAdornment: isFund ? <span>{getCurrencySymbol(values.currency)}</span> : '',
                  },
                }}
                fullWidth
              />
            </div>

            <div className={classes.inputsContainer}>
              <Input
                name="numberOfCodes"
                label="Number of codes"
                validate={validateNumberOfCodes}
                classes={{ root: classes.inputRoot }}
                inputProps={inputAccessibility('numberOfCodes')}
                fullWidth
              />

              <Input
                name="redeemLimitOfCodes"
                classes={{ root: classes.inputRoot }}
                label="Usage limit per code (0 = unlimited)"
                validate={nonNegativeWholeNumber}
                inputProps={inputAccessibility('redeemLimitOfCodes')}
                fullWidth
              />
            </div>

            <Input
              name="code"
              label="Custom code (optional, one code only)"
              validate={validateCustomCode}
              inputProps={{
                ...inputAccessibility('code'),
                helperText: 'Letters and digits, 4 to 20 characters',
              }}
            />

            <div>
              <div className={classes.date}>
                <span
                  className={
                    visibleErrors.startAtDate || dateOrderError ? classes.invalidLabel : undefined
                  }>
                  Start date
                </span>
                <div className={classes.datePickerContainer}>
                  <DateInputField
                    name="startAtDate.date"
                    validate={requiredStartDate}
                    label="Select date"
                    ariaLabel="Start date"
                    error={Boolean(startDateError || dateOrderError)}
                    errorDescriptionId={startDescriptionId}
                    pickerProps={{
                      disablePast: true,
                      maxDate: values.endAtDate.date?.format('YYYY-MM-DD'),
                    }}
                  />
                  <TimeInputField
                    name="startAtDate.time"
                    validate={requiredStartTime}
                    label="Select time"
                    ariaLabel="Start time"
                    error={Boolean(startDateError || dateOrderError)}
                    errorDescriptionId={startDescriptionId}
                  />
                </div>
              </div>
              {startDateError && (
                <FormHelperText error id={errorId('startAtDate')}>
                  {startDateError}
                </FormHelperText>
              )}
            </div>

            <div>
              <div className={classes.date}>
                <span
                  className={
                    visibleErrors.endAtDate || dateOrderError ? classes.invalidLabel : undefined
                  }>
                  End date
                </span>
                <div className={classes.datePickerContainer}>
                  <DateInputField
                    name="endAtDate.date"
                    validate={requiredEndDate}
                    label="Select date"
                    ariaLabel="End date"
                    error={Boolean(endDateError || dateOrderError)}
                    errorDescriptionId={endDescriptionId}
                    pickerProps={{
                      disablePast: true,
                    }}
                  />
                  <TimeInputField
                    name="endAtDate.time"
                    validate={requiredEndTime}
                    label="Select time"
                    ariaLabel="End time"
                    error={Boolean(endDateError || dateOrderError)}
                    errorDescriptionId={endDescriptionId}
                  />
                </div>
              </div>
              {endDateError && (
                <FormHelperText error id={errorId('endAtDate')}>
                  {endDateError}
                </FormHelperText>
              )}
              {dateOrderError && (
                <FormHelperText error id={errorId(DATE_ORDER_FIELD)}>
                  {dateOrderError}
                </FormHelperText>
              )}
            </div>

            {isFund && (
              <div className={classes.deferred}>
                <Checkbox
                  name="deferredEnabled"
                  label="Deferred credit (granted after the account spends on labels)"
                />

                {values.deferredEnabled && (
                  <>
                    {deliveryServicesState.status === 'loading' && (
                      <div className={classes.hint} role="status">
                        Loading carrier services...
                      </div>
                    )}

                    {deliveryServicesState.status === 'error' && (
                      <div>
                        <FormHelperText error role="alert">
                          {deliveryServicesState.error}
                        </FormHelperText>
                        <Button onClick={handleRetryDeliveryServices} disabled={submitting}>
                          Retry
                        </Button>
                      </div>
                    )}

                    {deliveryServicesState.status === 'loaded' && !serviceOptions.length && (
                      <div className={classes.hint} role="status">
                        No carrier services available for exclusion.
                      </div>
                    )}

                    <div className={classes.inputsContainer}>
                      <Input
                        name="deferred.spendThreshold"
                        label="Spend on labels before the credit is granted"
                        validate={positiveWholeNumber}
                        classes={{ input: classes.input, root: classes.inputRoot }}
                        inputProps={{
                          ...inputAccessibility('deferred.spendThreshold'),
                          InputProps: {
                            startAdornment: <span>{getCurrencySymbol(values.currency)}</span>,
                          },
                        }}
                        fullWidth
                      />

                      <Input
                        name="deferred.earnWindowDays"
                        label="Days after signup to earn it"
                        validate={positiveWholeNumber}
                        classes={{ root: classes.inputRoot }}
                        inputProps={inputAccessibility('deferred.earnWindowDays')}
                        fullWidth
                      />
                    </div>

                    <div
                      className={
                        visibleErrors['deferred.requireVerifiedCard']
                          ? classes.invalidField
                          : undefined
                      }>
                      <Switch
                        name="deferred.requireVerifiedCard"
                        label="Require a verified card"
                        inputProps={{
                          inputProps: {
                            'aria-invalid': Boolean(visibleErrors['deferred.requireVerifiedCard']),
                            'aria-describedby': visibleErrors['deferred.requireVerifiedCard']
                              ? errorId('deferred.requireVerifiedCard')
                              : undefined,
                          },
                        }}
                      />
                      {renderControlError('deferred.requireVerifiedCard')}
                    </div>

                    <div
                      className={
                        visibleErrors['deferred.excludeInternational']
                          ? classes.invalidField
                          : undefined
                      }>
                      <Switch
                        name="deferred.excludeInternational"
                        label="International labels do not count"
                        inputProps={{
                          inputProps: {
                            'aria-invalid': Boolean(visibleErrors['deferred.excludeInternational']),
                            'aria-describedby': visibleErrors['deferred.excludeInternational']
                              ? errorId('deferred.excludeInternational')
                              : undefined,
                          },
                        }}
                      />
                      {renderControlError('deferred.excludeInternational')}
                    </div>

                    <div
                      className={
                        visibleErrors['deferred.excludedServices']
                          ? classes.invalidField
                          : undefined
                      }>
                      <div className={classes.excludedServicesHeader}>
                        <FormLabel component="span" className={classes.excludedServicesLabel}>
                          Services that do not count
                        </FormLabel>
                        <Button
                          type="button"
                          size="small"
                          color="primary"
                          disabled={
                            submitting ||
                            deliveryServicesState.status !== 'loaded' ||
                            !serviceOptions.length
                          }
                          onClick={() =>
                            form.change(
                              'deferred.excludedServices',
                              allServicesSelected
                                ? []
                                : serviceOptions.map((option) => option.value)
                            )
                          }>
                          {allServicesSelected ? 'Deselect all' : 'Select all'}
                        </Button>
                      </div>
                      <Select
                        name="deferred.excludedServices"
                        classes={{ root: classes.excludedServicesControl }}
                        options={serviceOptions}
                        disabled={deliveryServicesState.status !== 'loaded'}
                        inputProps={{
                          error: Boolean(visibleErrors['deferred.excludedServices']),
                          SelectDisplayProps: {
                            'aria-label': 'Services that do not count',
                            'aria-labelledby': undefined,
                            'aria-invalid': Boolean(visibleErrors['deferred.excludedServices']),
                            'aria-describedby': visibleErrors['deferred.excludedServices']
                              ? errorId('deferred.excludedServices')
                              : undefined,
                          },
                          inputProps: {
                            name: 'deferred.excludedServices',
                          },
                          multiple: true,
                          displayEmpty: true,
                          classes: { selectMenu: classes.excludedServicesSelect },
                          renderValue: (selected: any) =>
                            (selected as string[]).length ? (
                              <div className={classes.excludedServicesValues}>
                                {(selected as string[]).map((value, index) => (
                                  <span key={value} className={classes.excludedService}>
                                    {serviceOptions.find((option) => option.value === value)
                                      ?.label || value.split(SERVICE_SEPARATOR).join(' ')}
                                    {index < (selected as string[]).length - 1 ? ', ' : ''}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              'No services excluded'
                            ),
                        }}
                      />
                      {renderControlError('deferred.excludedServices')}
                    </div>

                    <div className={classes.hint}>
                      Only accounts created after the campaign start can redeem a deferred code.
                    </div>
                  </>
                )}
              </div>
            )}
            {textFields
              .filter((field) => visibleErrors[field])
              .map((field) => (
                <span key={field} id={errorId(field)} hidden>
                  {visibleErrors[field]}
                </span>
              ))}
          </Dialog>
        );
      }}
    />
  );
};

export default CreateCampaignDialog;
